import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Save, Search, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { NIN_SLIP_LABELS } from '@/constants/ninSlipFeatures';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminVerificationService, getApiErrorMessage } from '@/services';
import type { AdminVerificationPricingRow } from '@/types/verification';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { notify } from '@/utils/notify';

interface PriceInputDraft {
  providerCost: string;
  subscriberPrice: string;
  agentPrice: string;
}

const NIN_METHOD_ORDER = ['by_nin_id', 'by_phone', 'by_tracking_id', 'by_demography'] as const;
const BVN_METHOD_ORDER = ['by_bvn_id', 'by_phone'] as const;
const NIN_SLIP_ORDER = ['regular_slip', 'standard_slip', 'premium_slip', 'advanced_slip'] as const;
const BVN_SLIP_ORDER = ['basic_slip', 'advanced_slip'] as const;

const METHOD_LABELS: Record<string, string> = {
  by_nin_id: 'By NIN ID',
  by_phone: 'By phone',
  by_tracking_id: 'By tracking ID',
  by_demography: 'By demography',
  by_bvn_id: 'By BVN ID',
};

const BVN_SLIP_LABELS: Record<string, string> = {
  basic_slip: 'Basic BVN',
  advanced_slip: 'Advanced BVN',
};

function toPriceInputs(row: AdminVerificationPricingRow): PriceInputDraft {
  return {
    providerCost: String(row.providerCost),
    subscriberPrice: String(row.subscriberPrice),
    agentPrice: String(row.agentPrice),
  };
}

function parsePriceField(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num) || num < 0) return null;
  return num;
}

function formatProfitPreview(costValue: string, sellValue: string) {
  const cost = parsePriceField(costValue);
  const sell = parsePriceField(sellValue);
  if (cost == null || sell == null) return '—';
  return `₦${Math.round((sell - cost) * 100) / 100}`;
}

function slipLabel(row: AdminVerificationPricingRow) {
  if (row.verificationType === 'nin') {
    return NIN_SLIP_LABELS[row.slipType as keyof typeof NIN_SLIP_LABELS] ?? row.slipType;
  }
  return BVN_SLIP_LABELS[row.slipType] ?? row.slipType;
}

function methodKey(row: AdminVerificationPricingRow) {
  return `${row.verificationType}:${row.verificationMethod}`;
}

export function AdminVerificationPricingPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [savingGroup, setSavingGroup] = useState<string | null>(null);
  const [rows, setRows] = useState<AdminVerificationPricingRow[]>([]);
  const [drafts, setDrafts] = useState<Record<number, AdminVerificationPricingRow>>({});
  const [priceInputs, setPriceInputs] = useState<Record<number, PriceInputDraft>>({});
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | 'nin' | 'bvn'>('ALL');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminVerificationService.listPricing();
      const pricing = res.data?.pricing ?? [];
      setRows(pricing);
      setDrafts(Object.fromEntries(pricing.map((row: AdminVerificationPricingRow) => [row.id, { ...row }])));
      setPriceInputs(Object.fromEntries(pricing.map((row: AdminVerificationPricingRow) => [row.id, toPriceInputs(row)])));
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load verification pricing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const rowByCombo = useMemo(() => {
    const map = new Map<string, AdminVerificationPricingRow>();
    for (const row of rows) {
      map.set(`${row.verificationType}:${row.verificationMethod}:${row.slipType}`, row);
    }
    return map;
  }, [rows]);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const types: Array<'nin' | 'bvn'> =
      typeFilter === 'ALL' ? ['nin', 'bvn'] : [typeFilter];

    return types.map((verificationType) => {
      const methodOrder = verificationType === 'nin' ? NIN_METHOD_ORDER : BVN_METHOD_ORDER;
      const slipOrder = verificationType === 'nin' ? NIN_SLIP_ORDER : BVN_SLIP_ORDER;

      const methods = methodOrder
        .map((verificationMethod) => {
          const slips = slipOrder
            .map((slipType) => rowByCombo.get(`${verificationType}:${verificationMethod}:${slipType}`))
            .filter((row): row is AdminVerificationPricingRow => Boolean(row))
            .filter((row) => {
              if (!q) return true;
              const haystack = [
                METHOD_LABELS[verificationMethod],
                slipLabel(row),
                row.description,
              ]
                .join(' ')
                .toLowerCase();
              return haystack.includes(q);
            });

          if (slips.length === 0) return null;
          return { verificationMethod, label: METHOD_LABELS[verificationMethod] ?? verificationMethod, slips };
        })
        .filter(Boolean) as Array<{
        verificationMethod: string;
        label: string;
        slips: AdminVerificationPricingRow[];
      }>;

      return { verificationType, methods };
    });
  }, [rowByCombo, search, typeFilter]);

  const stats = useMemo(() => {
    const nin = rows.filter((r) => r.verificationType === 'nin');
    const bvn = rows.filter((r) => r.verificationType === 'bvn');
    return {
      nin: { total: nin.length, active: nin.filter((r) => r.isActive).length },
      bvn: { total: bvn.length, active: bvn.filter((r) => r.isActive).length },
    };
  }, [rows]);

  async function saveRow(id: number, silent = false) {
    const draft = drafts[id];
    const inputs = priceInputs[id];
    if (!draft || !inputs) return false;

    const providerCost = parsePriceField(inputs.providerCost);
    const subscriberPrice = parsePriceField(inputs.subscriberPrice);
    const agentPrice = parsePriceField(inputs.agentPrice);
    if (providerCost == null || subscriberPrice == null || agentPrice == null) {
      if (!silent) notify.error('Enter valid non-negative prices');
      return false;
    }

    setSavingId(id);
    try {
      const res = await adminVerificationService.updatePricing(id, {
        providerCost,
        subscriberPrice,
        agentPrice,
        isActive: draft.isActive,
        description: draft.description,
      });
      const updated = res.data?.pricing;
      if (updated) {
        setRows((prev) => prev.map((row) => (row.id === id ? updated : row)));
        setDrafts((prev) => ({ ...prev, [id]: updated }));
        setPriceInputs((prev) => ({ ...prev, [id]: toPriceInputs(updated) }));
      }
      if (!silent) notify.success('Pricing updated');
      return true;
    } catch (error) {
      if (!silent) notify.error(getApiErrorMessage(error, 'Failed to update pricing'));
      return false;
    } finally {
      setSavingId(null);
    }
  }

  async function saveGroup(groupRows: AdminVerificationPricingRow[]) {
    const key = methodKey(groupRows[0]!);
    setSavingGroup(key);
    let saved = 0;
    for (const row of groupRows) {
      const ok = await saveRow(row.id, true);
      if (ok) saved += 1;
    }
    setSavingGroup(null);
    if (saved === groupRows.length) {
      notify.success(`Saved ${saved} slip tier${saved === 1 ? '' : 's'}`);
    } else if (saved > 0) {
      notify.error(`Saved ${saved} of ${groupRows.length} tiers — check invalid rows`);
    } else {
      notify.error('Nothing saved — check your prices');
    }
  }

  function toggleGroup(key: string) {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function renderPriceInput(
    rowId: number,
    field: keyof PriceInputDraft,
    className?: string,
  ) {
    const inputs = priceInputs[rowId];
    if (!inputs) return null;
    return (
      <input
        className={cn('w-full min-w-[4.5rem] rounded-lg border px-2 py-1.5 text-sm', shell.input, className)}
        value={inputs[field]}
        onChange={(e) =>
          setPriceInputs((prev) => ({
            ...prev,
            [rowId]: { ...prev[rowId]!, [field]: e.target.value },
          }))
        }
      />
    );
  }

  function renderMethodTable(
    verificationType: 'nin' | 'bvn',
    method: { verificationMethod: string; label: string; slips: AdminVerificationPricingRow[] },
  ) {
    const key = `${verificationType}:${method.verificationMethod}`;
    const collapsed = collapsedGroups[key] ?? false;
    const groupSaving = savingGroup === key;

    return (
      <Card key={key} className={cn('overflow-hidden', shell.card)}>
        <button
          type="button"
          onClick={() => toggleGroup(key)}
          className={cn(
            'flex w-full items-center justify-between gap-3 border-b px-4 py-3 text-left',
            shell.tableWrap,
          )}
        >
          <div className="flex min-w-0 items-center gap-2">
            {collapsed ? (
              <ChevronRight className={cn('h-5 w-5 shrink-0', shell.cardMuted)} />
            ) : (
              <ChevronDown className={cn('h-5 w-5 shrink-0', shell.cardMuted)} />
            )}
            <div>
              <p className="font-semibold">{method.label}</p>
              <p className={cn('text-xs', shell.cardMuted)}>
                {method.slips.length} slip tier{method.slips.length === 1 ? '' : 's'}
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            loading={groupSaving}
            loadingText="Saving..."
            onClick={(e) => {
              e.stopPropagation();
              void saveGroup(method.slips);
            }}
          >
            <Save className="mr-1 h-4 w-4" />
            Save all
          </Button>
        </button>

        {!collapsed && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={shell.tableHead}>
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Slip type</th>
                  <th className="px-3 py-3 text-left font-medium">Provider</th>
                  <th className="px-3 py-3 text-left font-medium">Subscriber</th>
                  <th className="px-3 py-3 text-left font-medium">Agent</th>
                  <th className="px-3 py-3 text-left font-medium">Sub profit</th>
                  <th className="px-3 py-3 text-left font-medium">Agent profit</th>
                  <th className="px-3 py-3 text-center font-medium">Active</th>
                  <th className="px-4 py-3 text-right font-medium"> </th>
                </tr>
              </thead>
              <tbody>
                {method.slips.map((row) => {
                  const draft = drafts[row.id];
                  const inputs = priceInputs[row.id];
                  if (!draft || !inputs) return null;
                  return (
                    <tr key={row.id} className={cn('border-t', shell.tableRow)}>
                      <td className="px-4 py-3 align-middle">
                        <p className="font-medium">{slipLabel(row)}</p>
                        {row.description && (
                          <p className={cn('text-xs', shell.cardMuted)}>{row.description}</p>
                        )}
                      </td>
                      <td className="px-3 py-3 align-middle">{renderPriceInput(row.id, 'providerCost')}</td>
                      <td className="px-3 py-3 align-middle">{renderPriceInput(row.id, 'subscriberPrice')}</td>
                      <td className="px-3 py-3 align-middle">{renderPriceInput(row.id, 'agentPrice')}</td>
                      <td className={cn('px-3 py-3 align-middle text-xs', shell.cardMuted)}>
                        {formatProfitPreview(inputs.providerCost, inputs.subscriberPrice)}
                      </td>
                      <td className={cn('px-3 py-3 align-middle text-xs', shell.cardMuted)}>
                        {formatProfitPreview(inputs.providerCost, inputs.agentPrice)}
                      </td>
                      <td className="px-3 py-3 text-center align-middle">
                        <input
                          type="checkbox"
                          checked={draft.isActive}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [row.id]: { ...prev[row.id]!, isActive: e.target.checked },
                            }))
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-right align-middle">
                        <Button
                          type="button"
                          size="sm"
                          loading={savingId === row.id}
                          loadingText="..."
                          onClick={() => void saveRow(row.id)}
                        >
                          Save
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-brand-green" />
          <h1 className="text-2xl font-bold">Verification Pricing</h1>
        </div>
        <p className={cn('mt-1 text-sm', shell.cardMuted)}>
          Fixed prices by lookup method and slip tier. Provider cost is what OTOBILL charges; set subscriber and agent
          selling prices per row.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Card className={cn('p-4', shell.card, typeFilter === 'nin' ? 'ring-2 ring-brand-green/40' : '')}>
          <p className={cn('text-xs uppercase tracking-wide', shell.cardMuted)}>NIN tiers</p>
          <p className="mt-1 text-2xl font-bold">{stats.nin.active}/{stats.nin.total}</p>
          <p className={cn('text-xs', shell.cardMuted)}>active pricing rows</p>
        </Card>
        <Card className={cn('p-4', shell.card, typeFilter === 'bvn' ? 'ring-2 ring-brand-green/40' : '')}>
          <p className={cn('text-xs uppercase tracking-wide', shell.cardMuted)}>BVN tiers</p>
          <p className="mt-1 text-2xl font-bold">{stats.bvn.active}/{stats.bvn.total}</p>
          <p className={cn('text-xs', shell.cardMuted)}>active pricing rows</p>
        </Card>
      </div>

      <Card className={cn('space-y-4 p-4', shell.card)}>
        <div className="relative">
          <Search className={cn('absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', shell.cardMuted)} />
          <input
            className={cn('w-full rounded-lg border py-2 pl-9 pr-3 text-sm', shell.input)}
            placeholder="Search method or slip type..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {(['ALL', 'nin', 'bvn'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTypeFilter(value)}
              className={cn(
                'rounded-full px-4 py-1.5 text-sm font-medium transition',
                typeFilter === value
                  ? 'bg-primary text-white'
                  : cn('border', shell.outlineBtn),
              )}
            >
              {value === 'ALL' ? 'All' : value.toUpperCase()}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Loader label="Loading verification pricing..." />
      ) : (
        <div className="space-y-8">
          {filteredGroups.map((section) => {
            if (section.methods.length === 0) return null;
            return (
              <section key={section.verificationType} className="space-y-3">
                <h2 className="text-lg font-bold">
                  {section.verificationType === 'nin' ? 'NIN verification' : 'BVN verification'}
                </h2>
                <div className="space-y-3">
                  {section.methods.map((method) =>
                    renderMethodTable(section.verificationType, method),
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
